<script>
import { counterMixin } from '@mixins/counter-mixin';
import resize from '@utils/image-sizer';
import CardMenu from '@components/client/card-menu.vue';
import CardMenuList from '@components/client/card-menu-list.vue';
import { workoutMethods, authComputed } from '@state/helpers';
import { cleverTapMixin } from '@mixins/cleverTap-mixin';
import cloneDeep from 'lodash/cloneDeep';

export default {
  components: {
    CardMenu,
    CardMenuList,
  },
  props: {
    workout: {
      type: Object,
      required: true,
      default: null,
    },
  },
  mixins: [counterMixin, cleverTapMixin],
  data() {
    return {
      placeholder: require('@assets/images/placeholder-photo-upload.svg'),
      visibleCircuitCount: 3,
      isMenuOpen: false,
    };
  },
  computed: {
    ...authComputed,
    truncateDescription() {
      return this.workout.description
        ? this.workout.description.length > 40
          ? this.workout.description.substring(0, 40) + '...'
          : this.workout.description
        : '';
    },
    hasWorkoutMetaData() {
      return (
        (this.workout &&
          this.workout.muscleGroups &&
          this.workout.muscleGroups.length) ||
        (this.workout.exerciseDifficulty &&
          this.workout.exerciseDifficulty.exerciseDifficulty_id)
      );
    },
    hasMuscleGroups() {
      return (
        this.workout &&
        this.workout.muscleGroups &&
        this.workout.muscleGroups.length
      );
    },
    hasDifficulty() {
      return (
        this.workout &&
        this.workout.exerciseDifficulty &&
        this.workout.exerciseDifficulty.exerciseDifficulty_id
      );
    },
    hasCircuits() {
      return this.workout.circuits && this.workout.circuits.length;
    },
    coverPhoto() {
      return this.workout &&
        this.workout.mediaAssets &&
        this.workout.mediaAssets.coverPhoto &&
        this.workout.mediaAssets.coverPhoto.url
        ? resize(this.workout.mediaAssets.coverPhoto.url, 550, 200)
        : this.placeholder;
    },
    duration() {
      const seconds = this.workout.timeToComplete;
      return seconds ? `${this.$_getFormattedSeconds(seconds)}` : null;
    },
    hiddenCircuitCount() {
      return this.workout.circuits.length - this.visibleCircuitCount;
    },
    isTrainer() {
      return (
        this.roles && this.roles.length > 0 && this.roles[0] === 'isTrainer'
      );
    },
  },
  mounted() {},
  methods: {
    ...workoutMethods,
    onDuplicateWorkout() {
      const payload = cloneDeep(this.workout);
      const label = 'Copy'.concat(' ', payload.label);
      delete payload._id;
      this.createWorkout({
        workoutObj: {
          ...payload,
          label,
          org_id: this.userOrgId,
          trainer_id: this.trainerId,
        },
      })
        .then((res) => {
          if (res && res.success) {
            this.toWorkoutDetail(res.programWorkout);
            if (
              this.currentUser &&
              this.currentUser.roles &&
              this.currentUser.roles.isTrainer &&
              res &&
              res.programWorkout
            ) {
              const workout = res.programWorkout._id;
              const trainer = this.trainerEmail;
              this.$_pushCleverTapEvent('Create Library Workout', {
                workout,
                trainer,
              });
            }
          }
        })
        .catch((e) => console.log(e));
    },
    truncateCircuitTitle(circuitTitle) {
      return circuitTitle
        ? circuitTitle.length > 20
          ? circuitTitle.substring(0, 20) + '...'
          : circuitTitle
        : '';
    },
    toWorkoutDetail(workout) {
      this.isMenuOpen = false;
      this.$router
        .push({
          name: 'workoutDetail',
          params: { id: workout._id, workout: workout },
        })
        .catch(() => {});
    },
    showDeets() {
      this.$refs.workoutCardDetail.style.width = `${this.$refs
        .workoutCardMinimal.clientWidth + 'px'}`;
      this.$refs.workoutCardMinimal.style.opacity = 0;
      this.$refs.workoutCardDetail.style.opacity = 1;
      this.$refs.workoutCardDetail.style.zIndex = 0;
    },
    hideDeets() {
      this.isMenuOpen = false;
      this.$refs.workoutCardDetail.style.opacity = 0;
      this.$refs.workoutCardDetail.style.zIndex = -1;
      this.$refs.workoutCardMinimal.style.opacity = 1;
    },
  },
};
</script>

<template>
  <div class="card-workout-container" @mouseleave="hideDeets">
    <div
      :class="[
        'card-workout-minimal',
        { mine: !isTrainer || (isTrainer && workout.trainer_id !== '0') },
      ]"
      @mouseover="showDeets"
      ref="workoutCardMinimal"
    >
      <div
        class="card-hero"
        :style="{ 'background-image': `url(${coverPhoto})` }"
      >
        <div v-if="duration" class="card-meta">
          <span>
            <span class="duration">{{ duration }}</span>
          </span>
        </div>
      </div>
      <div class="card-content">
        <h3 class="card-title">{{ workout.label }}</h3>
        <span class="card-meta" v-if="hasWorkoutMetaData">
          <span v-if="hasDifficulty">
            {{ workout.exerciseDifficulty.label }}
            <span v-if="hasMuscleGroups">|</span>
          </span>
          <span v-for="(group, i) in workout.muscleGroups" :key="i">
            {{ group.label
            }}<span v-if="i + 1 < workout.muscleGroups.length">,</span>
          </span>
        </span>
      </div>
    </div>

    <div
      class="card-workout-detail"
      ref="workoutCardDetail"
      @click.stop="toWorkoutDetail(workout)"
    >
      <div v-if="isTrainer" class="menu" @click.stop="isMenuOpen = !isMenuOpen">
        <div class="btn-wrapper">
          <div />
          <div />
          <div />
        </div>
      </div>
      <CardMenu v-if="isTrainer && isMenuOpen">
        <CardMenuList
          type="duplicate"
          target="workout"
          @handle-duplicate="onDuplicateWorkout"
        />
      </CardMenu>
      <div class="card-workout-detail-header">
        <div
          class="col-4 image-wrapper"
          :style="{ 'background-image': `url(${coverPhoto})` }"
        ></div>
        <div class="col-8">
          <h5>{{ truncateDescription }}</h5>
        </div>
      </div>

      <div
        class="workout-circuits-minimal"
        v-if="workout.circuits && workout.circuits.length"
      >
        <span class="workout-circuits-title">Circuits</span>
        <ul>
          <li
            v-for="(circuit, i) in workout.circuits"
            :key="i"
            :style="`display: ${i + 1 > visibleCircuitCount ? 'none' : 'flex'}`"
          >
            <span>{{ truncateCircuitTitle(circuit.label) }}</span>
            <span>x{{ circuit.circuitReps }}</span>
          </li>
          <li v-if="workout.circuits.length > visibleCircuitCount"
            >{{ hiddenCircuitCount }} more...</li
          >
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.card-workout-container {
  margin: 0 0 6rem 0;
  position: relative;

  &:hover {
    cursor: pointer;
  }
}

.card-workout-minimal {
  transition: opacity $duration-animation-base ease-in-out;

  .card-hero {
    align-items: flex-start;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: $base-border-radius-md;
    display: flex;
    height: 17rem;
    margin-bottom: 1.3rem;

    @include media('xxxl', null) {
      height: 20rem;
    }

    .card-meta {
      background: $color-card-program-meta-bg;
      border-radius: 0.4rem;
      color: white;
      font-size: 1.2rem;
      font-weight: 800;
      left: 1rem;
      letter-spacing: 0.1px;
      line-height: 1.6rem;
      mix-blend-mode: normal;
      opacity: 0.7;
      padding: 0.6rem 0.9rem;
      position: relative;
      text-transform: uppercase;
      top: 1rem;

      .duration {
        position: relative;
        top: 0.1rem;
      }
    }
  }

  .card-content {
    .card-title {
      -webkit-box-orient: vertical;
      display: block;

      /* Fallback for non-webkit */
      display: -webkit-box;
      font-family: $base-font-family;
      font-size: 2.1rem;
      font-style: normal;
      font-weight: 900;
      height: 2.1rem;
      letter-spacing: -0.3px;

      /* Fallback for non-webkit */
      -webkit-line-clamp: 1;
      line-height: 2.4rem;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .card-meta {
      text-transform: uppercase;
    }
  }
}

.card-workout-detail {
  background-color: white;
  border-radius: $base-border-radius-md;
  box-shadow: 0 0.4rem 0.8rem rgba($color-black-wo-rgb, 0.23);
  box-sizing: border-box;
  flex: 0 0 auto;
  flex-basis: 24%;
  height: 30rem;
  margin-bottom: 1.3rem;
  opacity: 0;
  padding: 2rem;
  position: absolute;
  top: -1.5rem;
  transition: opacity $duration-animation-base ease-in-out,
    z-index $duration-animation-base ease-in-out;
  z-index: -1;

  @include media('xxxl', null) {
    height: 29.3rem;
  }

  .menu {
    align-items: center;
    border-radius: 0.5rem;
    display: flex;
    justify-content: center;
    mix-blend-mode: normal;
    padding: 0.6rem 1.3rem;
    position: absolute;
    right: 1.5rem;
    top: 0.9rem;
    width: fit-content;
    z-index: 10;
    .btn-wrapper {
      align-items: flex-end;
      display: flex;
      flex-direction: column;
      height: fit-content;
      opacity: 0;
      transition: all 300ms;
      width: 100%;
      div {
        background-color: $color-medium-gray-alt;
        border-radius: 50%;
        height: 4px;
        margin-bottom: 2px;
        width: 4px;
      }
    }
    &:hover {
      background: rgb(218, 218, 218, 0.5);
      .btn-wrapper div {
        background-color: $color-heading-text;
      }
    }
  }
  .card-client-menu {
    max-width: 200px;
    right: 15px;
    top: 30px;
  }

  .card-workout-detail-header {
    display: flex;
    height: 7.1rem;
  }

  .image-wrapper {
    background-position: center;
    background-size: cover;
    border-radius: $base-border-radius;
  }

  .workout-circuits-minimal {
    margin-top: 1.6rem;

    .workout-circuits-title {
      font-size: 1rem;
      font-weight: 900;
      letter-spacing: 0.702381px;
      line-height: 1.9rem;
      text-transform: uppercase;
    }

    li {
      display: flex;
      font-size: 1.4rem;
      font-weight: 600;
      justify-content: space-between;
      letter-spacing: -0.0666664px;
      line-height: 3.4rem;
      margin-right: 15px;
      mix-blend-mode: normal;
      opacity: 0.7;

      @include media('xxxl', null) {
        padding: 0.3rem 0;
      }

      &:not(:last-child) {
        border-bottom: 1px solid $color-medium-gray;
      }
    }
  }
  &:hover {
    box-shadow: 0 0.4rem 0.8rem rgba($color-black-wo-rgb, 0.1);
    transform: scale(1.01);
    .menu {
      .btn-wrapper {
        opacity: 1;
      }
    }
  }
}
</style>
